import {Grid, Clickable, GridItem, Show, Hide} from 'platform/foundation';

import {ReactNode} from 'react';

import {isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {AuditParamDefinition} from '../../../types/AuditParamDefinition';
import {useFormRenderer} from '../../FinalForm/hooks/useFormRenderer';
import {useAuditImages} from '../hooks/useAuditImages';
import {useConditionContext} from '../hooks/useConditionContext';
import {useConditionUploadContext} from '../hooks/useConditionUploadContext';
import {getFormFieldName} from '../utils/getFormFieldName';
import {AuditImages} from './AuditImages';
import {UploadContext} from './UploadContext';
import {UploadControl} from './UploadControl';

interface AdditionalInformationProps extends TestIdProps {
  categoryId: string;
  additionalPhotos?: AuditParamDefinition;
  additionalComment?: AuditParamDefinition;
  beside?: boolean;
  uniqueKey: string;
  photoGrid?: number;
  hasPhotoError?: boolean;
}

export const AdditionalInformation = (props: AdditionalInformationProps) => {
  const {additionalComment, additionalPhotos, beside, categoryId} = props;
  const {Field} = useFormRenderer();
  const {isDisabledForUser} = useConditionContext();
  const isAdditionalCommentMandatory = additionalComment?.mandatory;

  return (
    <AdditionalInformationWrapper {...props}>
      <Grid
        columns={beside ? 2 : 1}
        data-testid={suffixTestId('additionalInformation', props)}
        verticalSpacing={0}
      >
        <Show when={additionalComment}>
          <GridItem span={1}>
            <Field
              as="nullableText"
              multiline
              parse={(value) => value || ''}
              name={getFormFieldName(categoryId, additionalComment?.id)}
              label={i18n.t('general.labels.note')}
              textareaProps={{minRows: 1}}
              disabled={isDisabledForUser}
              required={isAdditionalCommentMandatory}
              data-testid={suffixTestId('note', props)}
            />
          </GridItem>
        </Show>
        <GridItem span={beside ? 2 : 1}>
          <Show when={additionalPhotos}>
            <AdditionalPhotos
              {...props}
              data-testid={suffixTestId('additionalInformation-photos', props)}
            />
          </Show>
        </GridItem>
      </Grid>
    </AdditionalInformationWrapper>
  );
};

type AdditionalInformationWrapperProps = AdditionalInformationProps & {
  children: ReactNode;
};

const AdditionalInformationWrapper = ({
  categoryId,
  additionalPhotos,
  children,
}: AdditionalInformationWrapperProps) => {
  if (additionalPhotos) {
    return (
      <UploadContext categoryId={categoryId} paramDefinitionId={additionalPhotos.id} type="button">
        {children}
      </UploadContext>
    );
  }

  return <>{children}</>;
};

const AdditionalPhotos = (props: AdditionalInformationProps & TestIdProps) => {
  const {uploadImages} = useConditionUploadContext();
  const {assets} = useAuditImages();

  const hasAdditionalPhotos = isNilOrEmpty(assets) && isNilOrEmpty(uploadImages);

  return (
    <Clickable onContextMenu={(e) => e.preventDefault()}>
      <Show when={hasAdditionalPhotos}>
        <UploadControl
          type="button"
          isMultiple
          isMandatory={props.additionalPhotos?.mandatory}
          hasError={props.hasPhotoError}
          data-testid={suffixTestId('additionalPhotos', props)}
        />
      </Show>
      <Hide when={hasAdditionalPhotos}>
        {/* eslint-disable-next-line no-nested-ternary -- Pre-existing violation */}
        <Grid columns={isNotNil(props.photoGrid) ? props.photoGrid : props.beside ? 8 : 5}>
          <AuditImages
            isMultiple
            isAlwaysShowUpload
            ratio="4 / 3"
            data-testid={suffixTestId('additionalPhotos', props)}
          />
        </Grid>
      </Hide>
    </Clickable>
  );
};
